import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useRouter } from '@tanstack/react-router';
import { Inbox, Wrench } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import {
  downloadPcmpMarketingQueueXlsx,
  downloadPcmpProposalCorrectionXlsx,
  downloadPcmpMktDisbursementsXlsx,
  pcmpMarketingCountsQueryOptions,
  pcmpMarketingQueueQueryOptions,
} from '../../api/pcmpMarketing.api';
import { PCMP_MARKETING_TAB_LABELS } from '../../constants/pcmpMarketing.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { usePcmpMarketingFilters } from '../../hooks/usePcmpMarketingFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import type {
  PcmpMarketingQueueCounts,
  PcmpMarketingQueueItem,
  PcmpMarketingQueueTab,
  PcmpMarketingTab,
} from '../../types/pcmpMarketing.types';
import { PcmpStatusPill } from '../PcmpProposalDetail/PcmpStatusPill';
import { PcmpExportButton } from '../PcmpQueue/PcmpExportButton';
import { PcmpQueueFilterBar } from '../PcmpQueue/PcmpQueueFilterBar';
import { getPcmpMarketingColumns } from './PcmpMarketingColumns';
import { PcmpMarketingBatchesView } from './PcmpMarketingBatchesView';
import { PcmpMarketingRowActions } from './PcmpMarketingRowActions';
import { PcmpExplainedToggle, PcmpMarketingTabs } from './PcmpMarketingTabs';
import { PcmpProposalCorrectionList } from './PcmpProposalCorrectionList';

const route = getRouteApi('/_auth/pcmp/marketing/');

const EMPTY_MESSAGE: Record<PcmpMarketingQueueTab, string> = {
  AWAITING: 'Nothing is waiting for your action',
  ACTIONABLE: 'No proposals to approve, disburse or confirm',
  ALL: 'No proposals found',
  MIS_NOT_VERIFIED: 'No MIS Not Verified proposals',
  ON_HOLD: 'No proposals on hold',
};

function tabSubtitle(tab: PcmpMarketingTab, counts: PcmpMarketingQueueCounts): string {
  if (tab === 'PROPOSAL_CORRECTION') {
    return `${formatNumber(counts.proposalCorrection ?? 0)} open Proposal Correction requests`;
  }
  if (tab === 'BATCHES') {
    return `${formatNumber(counts.batches)} route batches holding your proposals`;
  }
  if (tab === 'MIS_NOT_VERIFIED') {
    return `${formatNumber(counts.misNotVerified)} awaiting your explanation · ${formatNumber(
      counts.misExplained,
    )} already explained`;
  }
  return `${formatNumber(counts.awaiting)} awaiting your action · ${formatNumber(
    counts.all,
  )} in your scope`;
}

export function PcmpMarketingQueueTable() {
  const router = useRouter();
  const controller = usePcmpMarketingFilters();
  const {
    tab,
    explained,
    page,
    limit,
    appliedFilters,
    queueParams,
    correctionParams,
    batchesParams,
  } = controller;
  const { can } = usePcmpPermissions();
  const canExport = can(PCMP_PERMISSION.VIEW, PCMP_PERMISSION.EXPORT);
  // The correction queue takes the same filters (not the tab or paging).
  const { tab: _tab, explained: _explained, page: _page, ...filterSearch } = route.useSearch();

  // Not fetched on Proposal Correction or Batches, which list from their own endpoints.
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpMarketingQueueQueryOptions(
      queueParams ?? { ...appliedFilters, tab: 'ALL', page, limit },
    ),
    enabled: queueParams !== null,
  });
  const { data: counts } = useQuery(pcmpMarketingCountsQueryOptions(appliedFilters));

  useKeepPageInRange(page, queueParams ? data?.meta.totalPages : undefined, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpMarketingQueueItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const columns = useMemo(
    () =>
      getPcmpMarketingColumns({
        showMisColumn: tab === 'MIS_NOT_VERIFIED',
        renderActions: (item) => <PcmpMarketingRowActions item={item} tab={tab} />,
      }),
    [tab],
  );

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Marketing Approval Queue</h1>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {counts ? tabSubtitle(tab, counts) : ' '}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every proposal tab exports its own rows under the filters, read-only
              viewers included (legacy LDS-820); Batches exports per batch. */}
          {batchesParams ? null : (
            <PcmpExportButton
              title={`${PCMP_MARKETING_TAB_LABELS[tab]}, under the filters above`}
              download={(date) =>
                queueParams
                  ? downloadPcmpMarketingQueueXlsx(
                      { ...appliedFilters, tab: queueParams.tab, explained },
                      date,
                    )
                  : downloadPcmpProposalCorrectionXlsx(appliedFilters, date)
              }
            />
          )}
          {canExport && queueParams ? (
            <PcmpExportButton
              label="Disbursed Excel"
              title="Proposals Marketing has paid out (proof uploaded), under the filters above"
              download={(date) => downloadPcmpMktDisbursementsXlsx(appliedFilters, date)}
            />
          ) : null}
          {counts && counts.correction > 0 ? (
            // Legacy showed the failed payments awaiting the chain beside this queue.
            <Link
              to="/pcmp/correction"
              search={filterSearch}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
            >
              <Wrench className="size-3.5" />
              Payment Correction
              <PcmpStatusPill tone="warning" className="tabular-nums">
                {formatNumber(counts.correction)}
              </PcmpStatusPill>
            </Link>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <PcmpMarketingTabs tab={tab} counts={counts} onTabChange={controller.setTab} />
        {tab === 'MIS_NOT_VERIFIED' ? (
          <PcmpExplainedToggle
            explained={explained}
            counts={counts}
            onChange={controller.setExplained}
          />
        ) : null}
      </div>

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-mkt" showPayoutFilters />

      {batchesParams ? (
        <PcmpMarketingBatchesView
          params={batchesParams}
          appliedFilters={appliedFilters}
          onPageChange={controller.setPage}
          onLimitChange={controller.setLimit}
        />
      ) : correctionParams ? (
        <PcmpProposalCorrectionList
          params={correctionParams}
          onPageChange={controller.setPage}
          onLimitChange={controller.setLimit}
        />
      ) : error ? (
        <ErrorState
          error={error}
          fallbackTitle={`Could not load ${PCMP_MARKETING_TAB_LABELS[tab]}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={data?.data ?? []}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage={EMPTY_MESSAGE[queueParams?.tab ?? 'ALL']}
          skeletonRowCount={Math.min(limit, 15)}
          getRowClassName={(item) =>
            (item.action && !item.action.isOptional) ||
            item.canMktDisburse ||
            item.canConfirmDisbursement
              ? 'bg-primary/5'
              : undefined
          }
          onRowClick={openProposal}
        />
      )}

      {queueParams && data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </div>
  );
}
